import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useMutationWithToast } from "@/shared/hooks/useMutationWithToast";
import { useConfirm } from "@/shared/hooks/use-confirm";
import { useToast } from "@/shared/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { ROLES } from "@/shared/lib/rbac";
import { Card, CardContent } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Plus, Settings2, Trash2 } from "lucide-react";
import { wecomAppService, type WeComApp, type WeComAppRequest } from "@/entities/wecom-app";
import { isAdvisorEnabled } from "./appFormUtils";
import { testConnectionFailureToast } from "../model/testConnectionToast";
import { IntegrationEndpoints } from "./IntegrationEndpoints";
import { CapabilitySection } from "./CapabilitySection";
import { WeComConfigForm } from "./WeComConfigForm";
import { KfBindingSheet } from "./KfBindingSheet";
import { WeComVerifyFilesSection } from "./WeComVerifyFilesSection";
import { usePersona } from "@/shared/brand/persona";

interface Props {
  tenantId: number;
}

/**
 * 企业微信接入统一配置(一租户一行模型):
 *   - 凭据卡(应用凭据 + 编辑)
 *   - 启用能力(AI客服 KF 绑定 + 军师工作台开关=identityMode)
 *   - 集成端点(回调URL / 主页URL / 授权域 + 测试连接)
 *   - 域名验证文件(WW_verify_*.txt 上传/列表/删除 + 自有域名指引)
 * 取 listApps 第一行为该租户配置;无行=空态引导。
 */
export function WeComUnifiedConfig({ tenantId }: Props) {
  const persona = usePersona();
  const { t } = useTranslation("wecom");
  const { toast } = useToast();
  const confirm = useConfirm();
  const { effectiveRole } = useAuth();
  const canManage = effectiveRole === ROLES.TENANT_ADMIN || effectiveRole === ROLES.PLATFORM_ADMIN;

  const [formOpen, setFormOpen] = useState(false);
  const [kfOpen, setKfOpen] = useState(false);

  const { data: apps = [] } = useQuery({
    queryKey: ["wecom-apps", tenantId],
    queryFn: () => wecomAppService.listApps(tenantId),
    enabled: !!tenantId,
  });
  const config: WeComApp | null = apps[0] ?? null;

  const { data: kfAccounts = [] } = useQuery({
    queryKey: ["wecom-kf-accounts", tenantId, config?.id],
    queryFn: () => wecomAppService.listKfAccounts(tenantId, config!.id),
    enabled: !!config?.id,
  });

  const saveMutation = useMutationWithToast({
    mutationFn: (req: WeComAppRequest) =>
      config
        ? wecomAppService.updateApp(tenantId, config.id, req)
        : wecomAppService.createApp(tenantId, req),
    successToast: () => ({
      title: config ? t("appList.updateSuccess") : t("appList.createSuccess"),
    }),
    errorToast: t("appList.operationFailed"),
    invalidateKeys: [["wecom-apps", tenantId]],
    onSuccess: () => setFormOpen(false),
  });

  const testMutation = useMutationWithToast({
    mutationFn: () => wecomAppService.testConnection(tenantId, config!.id),
    successToast: t("appList.testSuccess"),
    // B1 契约 ④(0927):凭据错现在是 400 BAD_REQUEST,默认码表标题会变「请求有误」;这里钉语境标题
    //   「企微连通失败」+ 后端人话(企业 ID 或 Secret 填错),不再拼「请稍后重试」尾巴。
    errorToast: (err) => testConnectionFailureToast(err, t("appList.testFailed")),
  });

  const deleteMutation = useMutationWithToast({
    mutationFn: () => wecomAppService.deleteApp(tenantId, config!.id),
    successToast: t("appList.deleteSuccess"),
    errorToast: t("appList.operationFailed"),
    invalidateKeys: [["wecom-apps", tenantId]],
  });

  // 军师开关:直接复用 saveMutation,只翻 identityMode(密钥留空=保持)。
  const toggleAdvisor = (next: boolean) => {
    if (!config) return;
    saveMutation.mutate({
      corpId: config.corpId,
      agentId: config.agentId,
      appSecret: "",
      callbackToken: "",
      encodingAesKey: "",
      label: config.label,
      enabled: config.enabled,
      module: "customer_service",
      callbackDomain: config.callbackDomain ?? "",
      companyName: config.companyName ?? null,
      identityMode: next ? "TENANT_CHAIN" : "MEMBER_CHANNEL",
    });
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: t("appList.copied", { label }) });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <h3 className="text-base font-semibold tracking-tight">{t("appList.unifiedTitle")}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{t("appList.unifiedSubtitle")}</p>
        </div>
        {canManage && !config && (
          <Button size="sm" onClick={() => setFormOpen(true)}>
            <Plus className="h-3.5 w-3.5 mr-1" />
            {t("appList.startConfigure")}
          </Button>
        )}
        {canManage && config && (
          <div className="flex gap-1">
            <Button size="sm" variant="outline" onClick={() => setFormOpen(true)}>
              <Settings2 className="h-3.5 w-3.5 mr-1" />
              {t("appList.editConfig")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-muted-foreground hover:text-destructive"
              disabled={deleteMutation.isPending}
              onClick={async () => {
                const ok = await confirm({
                  title: "删除企业微信配置?",
                  description: `删除后本租户的企微通道(${persona.title}工作台/客服舱/通知)将整体断开,需重新配置才能恢复。`,
                  confirmText: t("appCard.delete"),
                  variant: "destructive",
                });
                if (ok) deleteMutation.mutate();
              }}
              aria-label={t("appCard.delete")}
              title={t("appCard.delete")}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>

      {!config ? (
        <Card>
          <CardContent className="p-10 text-center text-sm text-muted-foreground">
            {canManage
              ? t("appList.emptyUnified")
              : "企业微信配置由租户管理员管理。如需接入或调整，请联系管理员。"}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {/* 凭据摘要 */}
          <Card>
            <CardContent className="p-4 space-y-1">
              <div className="text-sm font-medium">{config.label || config.corpId}</div>
              <div className="text-2xs text-muted-foreground font-mono truncate">
                {config.corpId}
              </div>
            </CardContent>
          </Card>

          {/* 启用能力 */}
          <CapabilitySection
            advisorEnabled={isAdvisorEnabled(config)}
            kfAccountCount={kfAccounts.length}
            canManage={canManage}
            advisorPending={saveMutation.isPending}
            onToggleAdvisor={toggleAdvisor}
            onManageKf={() => setKfOpen(true)}
          />

          {/* 集成端点 */}
          <IntegrationEndpoints
            callbackUrl={config.callbackUrl}
            homepageUrl={config.homepageUrl}
            callbackDomain={config.callbackDomain}
            onCopy={copyToClipboard}
            onTest={() => testMutation.mutate()}
            isTesting={testMutation.isPending}
            canTest={canManage}
          />
        </div>
      )}

      {/* 域名指引 + WW_verify 验证文件(OEM D1):配可信域名前就要用,不依赖凭据已配 */}
      <WeComVerifyFilesSection
        canRead={canManage || effectiveRole === ROLES.TENANT_MANAGER}
        canWrite={canManage}
      />

      <WeComConfigForm
        open={formOpen}
        editing={config}
        pending={saveMutation.isPending}
        onSubmit={(req) => saveMutation.mutate(req)}
        onOpenChange={setFormOpen}
      />
      <KfBindingSheet
        tenantId={tenantId}
        appId={config?.id ?? null}
        open={kfOpen}
        onOpenChange={setKfOpen}
      />
    </div>
  );
}
